/**
 * Auth Slice
 *
 * Manages authentication state.
 * Login is handled by the MMT global header; this slice tracks
 * the resulting cookie-based auth state and handles logout.
 */
import { createSlice } from '@reduxjs/toolkit';
import { logoutThunk, checkAuthStatusThunk } from '../thunks/authThunks';

// ============================================
// State Types
// ============================================

export interface AuthState {
  readonly isLoggedIn: boolean;
  readonly isLoading: boolean;
  readonly isAuthChecked: boolean;
}

// ============================================
// Initial State
// ============================================

export const initialAuthState: AuthState = {
  isLoggedIn: false,
  isLoading: false,
  isAuthChecked: false,
};

// ============================================
// Slice
// ============================================

const authSlice = createSlice({
  name: 'auth',
  initialState: initialAuthState,
  reducers: {
    resetAuthState: () => initialAuthState,
  },
  extraReducers: (builder) => {
    // Check Auth Status
    builder
      .addCase(checkAuthStatusThunk.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(checkAuthStatusThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthChecked = true;
        state.isLoggedIn = action.payload.isLoggedIn;
      })
      .addCase(checkAuthStatusThunk.rejected, (state) => {
        state.isLoading = false;
        state.isAuthChecked = true;
        state.isLoggedIn = false;
      });

    // Logout
    builder
      .addCase(logoutThunk.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logoutThunk.fulfilled, (state) => {
        state.isLoggedIn = false;
        state.isLoading = false;
      })
      .addCase(logoutThunk.rejected, (state) => {
        state.isLoading = false;
      });
  },
});

// ============================================
// Actions & Reducer
// ============================================

export const { resetAuthState } = authSlice.actions;
export const authReducer = authSlice.reducer;
